// Which steps of a workflow tracker to show when the bar is too narrow for all
// of them. Pure functions, so the rule is testable without a layout engine.

/** How much of the tracker is on screen. */
export type StepLevel = 'all' | 'around' | 'window' | 'current';

/** Room kept for the last crumb of the trail: it keeps priority over the tracker. */
export const TRAIL_RESERVE_PX = 160;

/**
 * Indexes of the steps to show. 'all' is every step. 'window' is the current
 * step and the NEXT one (when the current one is the last, the previous and the
 * current). 'current' is the current step alone, for the narrowest bars where
 * even two chevrons do not fit next to the buttons.
 */
export function visibleStepIndexes(count: number, currentIndex: number, level: StepLevel): number[] {
  if (count <= 0) return [];
  const all = Array.from({ length: count }, (_, i) => i);
  if (level === 'all' || count === 1) return all;
  const cur = Math.min(Math.max(currentIndex, 0), count - 1);
  if (level === 'current') return [cur];
  // The previous step too, when there is room for it.
  if (level === 'around') return [cur - 1, cur, cur + 1].filter(i => i >= 0 && i < count);
  return cur === count - 1 ? [cur - 1, cur] : [cur, cur + 1];
}

function sum(widths: number[], indexes: number[]): number {
  return indexes.reduce((total, i) => total + (widths[i] ?? 0), 0);
}

/**
 * The widest level that fits `available` pixels, given the natural width of
 * every step (measured while all of them were on screen).
 */
export function chooseStepLevel(widths: number[], currentIndex: number, available: number): StepLevel {
  const count = widths.length;
  const levels: StepLevel[] = ['all', 'around', 'window', 'current'];
  for (const level of levels) {
    // 'around' is the same set as 'window' at either end of the flow.
    if (level === 'around' && visibleStepIndexes(count, currentIndex, 'around').length === visibleStepIndexes(count, currentIndex, 'window').length) continue;
    if (sum(widths, visibleStepIndexes(count, currentIndex, level)) <= available) return level;
  }
  return 'current';
}

/** "Step 2 of 4: Assigned" */
export function stepAnnouncement(index: number, count: number, label: string): string {
  return `Step ${index + 1} of ${count}: ${label}`;
}
